import { array, option, record } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { createSelector } from 'reselect';
import { getCurrentServiceDateCategoryPricing } from '../modules/pricing/views/servicePricing';
import { isVoucherDirty } from '../modules/resourceOrder/selectors';
import { getEnterpriseAvailabilityCategoryIds } from '../modules/serviceAvailability/selectors';
import { ApplicationState } from '../types';
import { WithEnterpriseId } from '../types/selectors/selectorPropTypes';
import { getSelectedCityEnterprises } from './views/selectedCityEnterprises';

const getCurrentEnterprisePricing = (state: ApplicationState, { enterpriseId }: WithEnterpriseId) => {
    const categoryIds = getEnterpriseAvailabilityCategoryIds(state, { enterpriseId });
    const pricings = categoryIds.map((categoryId) =>
        option.toNullable(getCurrentServiceDateCategoryPricing(state, { categoryId }))
    );
    return pricings;
};

const isSelectedEnterprisePricingFetching = createSelector(
    getCurrentEnterprisePricing,
    array.some((pricing) => pricing?.isFetching ?? false)
);

const hasSelectedEnterprisePricingFailed = createSelector(
    getCurrentEnterprisePricing,
    array.some((pricing) => pricing?.hasFailed ?? false)
);

const hasSelectedEnterprisePricingNoRates = createSelector(getCurrentEnterprisePricing, (pricing) => {
    if (pricing == null || pricing.length === 0) {
        return true;
    }
    return pricing.some((pricingItem) => {
        if (pricingItem == null) {
            return true;
        }
        const { rates } = pricingItem;
        return record.isEmpty(rates);
    });
});
const shouldGetSelectedEnterprisePricing = createSelector(
    isSelectedEnterprisePricingFetching,
    hasSelectedEnterprisePricingFailed,
    isVoucherDirty,
    hasSelectedEnterprisePricingNoRates,
    (isFetching, hasFailed, voucherDirty, hasNoRates) => !isFetching && (hasFailed || voucherDirty || hasNoRates)
);

export const shouldGetSelectedCityPricing = (state: ApplicationState) =>
    pipe(
        getSelectedCityEnterprises(state, {}),
        record.some(({ id: enterpriseId }) => shouldGetSelectedEnterprisePricing(state, { enterpriseId }))
    );
